<template>
  <div class="flex flex-col flex-start gap-2 w-full">
    <NCheckbox v-model:checked="isHomePage" @update:checked="onHomePageToggle">
      {{ t('canvasUI.canvasBuilder.canvasSettings.form.messages.makeThisHome') }}
    </NCheckbox>

    <div v-if="isSectionList && isHomePage && sections.length > 0" class="mt-2">
      <NSelect
        v-model:value="selectedSectionId"
        :options="sectionOptions"
        :placeholder="t('canvasUI.canvasBuilder.canvasSettings.form.placeholder.selectSection')"
        @update:value="onSectionSelect"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { NCheckbox, NSelect } from 'naive-ui'
import { computed, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { ComponentTypes, type SectionListProps } from '@canvas-builder/types/canvas'

const { t } = useI18n()
const { activeSettingsNode, activeCanvasSections, updateHomePageSettings, canvasHomePage } = useCanvas()

// Check if the current component is a section list
const isSectionList = computed(() => activeSettingsNode.value?.type === ComponentTypes.SectionList)

// Get sections from section list component
const sections = computed(() => {
  if (isSectionList.value && activeSettingsNode.value?.data) {
    return (activeSettingsNode.value.data as SectionListProps).sections || []
  }
  return []
})

// Convert sections to options for select dropdown
const sectionOptions = computed(() => {
  return sections.value.map((section) => {
    // Get section component by ID
    const sectionComponent = activeCanvasSections.value[section.id]
    // Safely access name or use section ID as fallback

    return {
      label: sectionComponent.name,
      value: section.id,
    }
  })
})

// Check if the current component is set as home
const isHomePage = ref(false)
const selectedSectionId = ref<string | null>(null)

// When home page checkbox is toggled
function onHomePageToggle(checked: boolean) {
  if (checked && activeSettingsNode.value?.id) {
    // Set current component as home
    const homePage = {
      component_id: activeSettingsNode.value.id,
      section_id: isSectionList.value && sections.value.length > 0 ? sections.value[0].id : null,
    }
    selectedSectionId.value = homePage.section_id
    updateHomePageSettings(homePage)
  } else {
    // Remove home page setting
    updateHomePageSettings(null)
  }
}

// When section is selected for home page
function onSectionSelect(sectionId: string) {
  if (!isHomePage.value || !activeSettingsNode.value?.id) return

  selectedSectionId.value = sectionId
  updateHomePageSettings({
    component_id: activeSettingsNode.value.id,
    section_id: sectionId,
  })
}

onMounted(() => {
  if (!activeSettingsNode.value) {
    console.warn('[CanvasHomeSettings]: no activeSettingsNode')
    return
  }

  // Check if this component is set as home
  const homePageSettings = canvasHomePage.value || null

  isHomePage.value = homePageSettings?.component_id === activeSettingsNode.value.id
  selectedSectionId.value = homePageSettings?.section_id || null

  // If this is a section list and it's home, but no section is selected, select the first one
  if (isHomePage.value && isSectionList.value && !selectedSectionId.value && sections.value.length > 0) {
    // Use the first section as the default
    selectedSectionId.value = sections.value?.[0]?.id
  }
})
</script>
